import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Observable,
  catchError,
  concatMap,
  filter,
  of,
  retry,
  take,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { NetworkStatusService } from './network-status.service';

/**
 * Resiliencia do refresh de token: um blip de rede no /connect/token nao pode
 * apagar a sessao.
 *
 * O problema: em rede instavel (4G oscilando, troca de torre, WebView dormindo)
 * o refresh do token falha por transporte (status 0 / timeout / 5xx) e o ABP
 * trata isso como "sessao invalida" e desloga o usuario. No app isso aparecia
 * como desconexao a cada 5-10 min.
 *
 * O angular-oauth2-oidc dispara o refresh via Angular HttpClient
 * (oAuthService.refreshToken() -> this.http.post(tokenEndpoint, ...)), entao um
 * interceptor consegue se interpor ANTES de o erro chegar nos caminhos
 * destrutivos do ABP.
 *
 * Os 3 caminhos que o ABP usa pra destruir a sessao quando o refresh falha
 * (pesquisados em @abp/ng.oauth/.../strategies):
 *   1. AuthFlowStrategy.init(): assina o evento `token_refresh_error` e chama
 *      navigateToLogin() em QUALQUER erro. Nao e filtravel.
 *   2. AuthPasswordFlowStrategy.refreshToken(): faz
 *      `.catch(() => { clearOAuthStorage(); rememberMeService.remove(); })`.
 *      Nao e filtravel.
 *   3. AuthFlowStrategy.listenToOauthErrors(): em todo OAuthErrorEvent chama
 *      clearOAuthStorage(), a menos que OAuthErrorFilterService.run(err)
 *      retorne true. Esse e filtravel (ver oauth-error-filter-registration.ts).
 *
 * Nao da pra "estender a estrategia" de forma limpa: o oauth.service do ABP
 * instancia a estrategia por uma const hardcoded (AUTH_FLOW_STRATEGY.Password),
 * nao por DI. Sobrescrever exigiria forkar/monkey-patchar internals do ABP,
 * fragil e quebra em update. Por isso atacamos o GATILHO na nossa camada.
 *
 * Estrategia deste interceptor: identifica o request de refresh
 * (POST no tokenEndpoint com grant_type=refresh_token) e, em erro de
 * transporte, faz retry com backoff esperando a rede voltar (via
 * NetworkStatusService). So deixa o erro propagar se for definitivo
 * (invalid_grant 400, etc.) ou se esgotar o orcamento de tentativas. Assim o
 * blip e absorvido antes de chegar nos caminhos 1/2/3.
 *
 * Trade-off (decisao consciente): cobre o blip transitorio (o bug). NAO cobre
 * queda SUSTENTADA: se o retry esgota, o ABP ainda desloga via caminhos 1/2.
 * Cobrir isso exigiria sobrescrever a estrategia (arriscado), que ficou de fora
 * por decisao (alinha com o login stock do app).
 */
@Injectable()
export class RefreshTokenResilienceInterceptor implements HttpInterceptor {
  /** Numero maximo de novas tentativas apos a falha inicial. */
  private static readonly MAX_RETRIES = 5;
  /** Backoff base; cresce exponencial ate MAX_BACKOFF_MS. */
  private static readonly BASE_BACKOFF_MS = 2_000;
  private static readonly MAX_BACKOFF_MS = 16_000;
  /**
   * Teto de espera pela rede voltar em cada tentativa. Limita o tempo total
   * pra que uma queda sustentada acabe propagando o erro (ver trade-off) em vez
   * de deixar o refresh pendurado pra sempre.
   */
  private static readonly NETWORK_WAIT_TIMEOUT_MS = 20_000;

  /** Endpoint de token do OpenIddict: <issuer>/connect/token. */
  private readonly tokenEndpoint =
    environment.oAuthConfig.issuer.replace(/\/+$/, '') + '/connect/token';

  constructor(private readonly networkStatus: NetworkStatusService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshTokenRequest(request)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      retry({
        count: RefreshTokenResilienceInterceptor.MAX_RETRIES,
        delay: (error, retryCount) => {
          // Erro definitivo (invalid_grant 400, etc.): nao adianta tentar de
          // novo, a sessao realmente acabou. Propaga pro ABP deslogar.
          if (!this.isTransientTransportError(error)) {
            return throwError(() => error);
          }

          const backoffMs = Math.min(
            RefreshTokenResilienceInterceptor.BASE_BACKOFF_MS * 2 ** (retryCount - 1),
            RefreshTokenResilienceInterceptor.MAX_BACKOFF_MS,
          );

          // Espera a rede voltar e respeita o backoff antes de reenviar. Se a
          // rede nao voltar dentro do teto, propaga o erro original (queda
          // sustentada -> trade-off).
          return this.waitForNetwork().pipe(
            timeout({ first: RefreshTokenResilienceInterceptor.NETWORK_WAIT_TIMEOUT_MS }),
            catchError(() => throwError(() => error)),
            // backoff so depois que ha rede, pra nao queimar tentativa offline
            concatMap(() => timer(backoffMs)),
          );
        },
      }),
    );
  }

  private isRefreshTokenRequest(request: HttpRequest<unknown>): boolean {
    if (request.method !== 'POST' || !request.url.startsWith(this.tokenEndpoint)) {
      return false;
    }
    return this.getGrantType(request) === 'refresh_token';
  }

  private getGrantType(request: HttpRequest<unknown>): string | null {
    const body = request.body;
    if (body instanceof HttpParams) {
      return body.get('grant_type');
    }
    if (typeof body === 'string') {
      return new URLSearchParams(body).get('grant_type');
    }
    return null;
  }

  /** So tenta de novo em falha de transporte: sem resposta (0) ou erro de servidor (5xx). */
  private isTransientTransportError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);
  }

  /** Emite uma vez assim que ha conexao (imediato se ja online). */
  private waitForNetwork(): Observable<boolean> {
    if (this.networkStatus.isConnected) {
      return of(true);
    }
    return this.networkStatus.connected$.pipe(
      filter(connected => connected),
      take(1),
    );
  }
}
